import { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Bold, 
  Italic, 
  Underline, 
  Type,
  Palette,
  AlignLeft,
  AlignCenter,
  AlignRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function RichTextEditor({ value, onChange, className }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (editorRef.current) {
      const sanitized = DOMPurify.sanitize(value || '', {
        ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'span', 'br', 'p', 'div', 'font'],
        ALLOWED_ATTR: ['style', 'color', 'size', 'face'],
      });
      if (editorRef.current.innerHTML !== sanitized) {
        editorRef.current.innerHTML = sanitized;
      }
    }
  }, [value]);


  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const applyFormat = (command: string, value?: string) => {
    if (command === 'foreColor' && value) {
      // Apply color using modern approach
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        document.execCommand('styleWithCSS', false, 'true');
        document.execCommand('foreColor', false, value);
        document.execCommand('styleWithCSS', false, 'false');
      }
    } else {
      document.execCommand(command, false, value);
    }
    editorRef.current?.focus();
    handleInput();
  };

  const colors = [
    { name: 'Foreground', value: 'hsl(var(--foreground))' },
    { name: 'Primary', value: 'hsl(var(--primary))' },
    { name: 'Secondary', value: 'hsl(var(--secondary))' },
    { name: 'Accent', value: 'hsl(var(--soft-accent))' },
    { name: 'Muted', value: 'hsl(var(--muted-foreground))' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Orange', value: '#f97316' },
  ];

  const fontSizes = [
    { name: 'Small', value: '3' },
    { name: 'Normal', value: '4' },
    { name: 'Large', value: '5' },
    { name: 'X-Large', value: '6' },
    { name: 'XX-Large', value: '7' },
  ];

  return (
    <div className={cn("border rounded-lg overflow-hidden", className)}>
      {/* Toolbar */}
      <div className="bg-muted/50 border-b p-2 flex flex-wrap gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('bold')}
          className="h-8 w-8 p-0"
        >
          <Bold className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('italic')}
          className="h-8 w-8 p-0"
        >
          <Italic className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('underline')}
          className="h-8 w-8 p-0"
        >
          <Underline className="h-4 w-4" />
        </Button>

        <div className="w-px h-8 bg-border mx-1" />

        {/* Font Size */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2"
            >
              <Type className="h-4 w-4" />
              <span className="text-xs">Size</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-40 p-2">
            <div className="space-y-1">
              {fontSizes.map((size) => (
                <Button
                  key={size.value}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => applyFormat('fontSize', size.value)}
                >
                  {size.name}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Color */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2"
            >
              <Palette className="h-4 w-4" />
              <span className="text-xs">Color</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-2">
            <div className="grid grid-cols-2 gap-1">
              {colors.map((color) => (
                <Button
                  key={color.name}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="justify-start gap-2"
                  onClick={() => applyFormat('foreColor', color.value)}
                >
                  <div 
                    className="w-4 h-4 rounded border" 
                    style={{ backgroundColor: color.value }}
                  />
                  <span className="text-xs">{color.name}</span>
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <div className="w-px h-8 bg-border mx-1" />

        {/* Alignment */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('justifyLeft')}
          className="h-8 w-8 p-0"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('justifyCenter')}
          className="h-8 w-8 p-0"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('justifyRight')}
          className="h-8 w-8 p-0"
        >
          <AlignRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={cn(
          "min-h-[200px] p-4 focus:outline-none bg-background text-foreground",
          isFocused && "ring-2 ring-primary ring-offset-2"
        )}
        suppressContentEditableWarning
      />
    </div>
  );
}